import './order_dispatch.css'
import { ThirdPartyDispatchModal } from '@/pages/OrderManagement/components/Grid/DetailModals/ThirdpartyDispatchModal'
import { useOrders } from '@api/useOrders'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { OptimizedBanner } from '@components/OrderDispatch/OptimizedBanner'
import { OptimizerWizard } from '@components/OrderDispatch/OptimizerWizard'
import { OrderModal } from '@components/OrderWizard/OrderModal'
import { Vertical } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { useModelConfig } from '@hooks/useModelConfig'
import { usePanelControls } from '@hooks/usePanelControls'
import { useRouteOptimization } from '@hooks/useRouteOptimization'
import { useQueryClient } from '@tanstack/react-query'
import type { GridApi } from 'ag-grid-community'
import { Row, Splitter } from 'antd'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { DndProvider } from 'react-dnd'
import Backend from 'react-dnd-html5-backend'
import { UpdateHistoryDrawer } from '../_SiteWide/AuditHistory/UpdateHistoryDrawer'
import { OrderDrawer } from '../_SiteWide/OrderDisplay/OrderDrawer'
import { AcceptedOrdersContextProvider, useAcceptedOrdersContext } from './Contexts/AcceptedOrdersContext'
import { ScheduleContextProvider, useScheduleContext } from './Contexts/ScheduleContext'
import { OrdersGrid } from './OrderGrid/OrdersGrid'
import { DSUReportDrawer } from './TripGrid/DSUReport/DSUReportDrawer'
import { DispatchToolbar } from './TripGrid/DispatchToolbar'
import { OrderLinkWizard } from './TripGrid/OrderLinkWizard'
import { TripGrid } from './TripGrid/TripGrid'
import { removeOrderFromCache } from '@/api/useOrders/util'

type OrderDispatchRowPinningCtxType = {
  rowPinningApiRef: React.MutableRefObject<any> | null
}

const OrderDispatchRowPinningCtx = createContext<OrderDispatchRowPinningCtxType>({
  rowPinningApiRef: null,
})

export function useOrderDispatchRowPinning() {
  return useContext(OrderDispatchRowPinningCtx)
}

function OrderDispatchRowPinningProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const rowPinningApiRef = useRef()

  return (
    <OrderDispatchRowPinningCtx.Provider
      value={{
        rowPinningApiRef,
      }}
    >
      {children}
    </OrderDispatchRowPinningCtx.Provider>
  )
}

export function OrderDispatch() {
  return (
    <DndProvider backend={Backend}>
      <OrderDispatchRowPinningProvider>
        <AcceptedOrdersContextProvider>
          <ScheduleContextProvider>
            <OrderDispatchContents />
          </ScheduleContextProvider>
        </AcceptedOrdersContextProvider>
      </OrderDispatchRowPinningProvider>
    </DndProvider>
  )
}

function OrderDispatchContents() {
  const ordersGridRef = useRef<GridApi>()
  const {
    cellCache,
    setSchedulePayload,
    refetchDriverSchedule,
    loadingSchedule,
    checkForStatusUpdates,
    schedules,
    schedulePayload,
    selectedDepots,
    handleDepotChange,
  } = useScheduleContext()
  const { panelHeights, updatePanelHeights, isHorizontalLayout, toggleLayout } = usePanelControls(cellCache)
  const queryClient = useQueryClient()
  const [showingOptimizerWizard, setShowingOptimizerWizard] = useState(false)
  const [showingTPDModal, setShowingTPDModal] = useState(false)
  const [showingOrderModal, setShowingOrderModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState()
  const [linkWizardActive, setLinkWizardActive] = useState(false)
  const [linkSelectedSchedule, setLinkSelectedSchedule] = useState()
  const [orderHistoryStoreId, setOrderHistoryStoreId] = useState()
  const [auditOrderId, setAuditOrderId] = useState()
  const [showingDSUDrawer, setShowingDSUDrawer] = useState(false)
  const [optimizerMode, setOptimizerMode] = useState('')
  const [statusUpdateRequest, setStatusUpdateRequest] = useState({
    driver_id: undefined,
    shift_start: undefined,
    shift_end: undefined,
  })
  const { config } = useModelConfig()
  const [orderId, setOrderId] = useState<Gravitate.Order['id'] | undefined>()
  const {
    useOrderByIdQuery,
    useDeliveryRequestAcceptManualQuery,
    autoAcceptOrderRequestMutation,
    cancelOrderRequestMutation,
  } = useOrders()
  const { data: tpdOrder } = useOrderByIdQuery(orderId)
  const { metadata, setOrdersCollapsed, updateOrder, orders, removeOrder, refetchOrders } = useAcceptedOrdersContext()
  const { value: tripTimeDisplay, setValue: setTripTimeDisplay } = useLocalStorage(
    'dispatch-view-settings::trip-time-display',
    'compact'
  )
  const { getSetting } = useUserSettings()
  const { userRunState } = useRouteOptimization()
  const { scopes } = useSecurityContext()
  const canOptimize = scopes?.order_dispatch?.dispatch_optimization

  useEffect(() => {
    checkForStatusUpdates()
    getSetting('dispatch-view-settings::trip-time-display').then((value) => {
      if (value === 'explicit') setTripTimeDisplay(`${value}`)
    })
  }, [])

  const triggerSelection = (orderId, assignedOrder) => {
    setShowingOrderModal(true)
    setSelectedOrder(orderId, assignedOrder)
  }

  const setRequestedOrderId = (requestId) => {
    setShowingOrderModal(true)
    setSelectedRequestOrderId(requestId)
  }

  const [previousPayload, setPreviousPayload] = useState<{ payload: unknown; depots: string[] }>()

  const triggerLinkWizard = (schedule, tripIndex) => {
    setPreviousPayload({ payload: { ...schedulePayload }, depots: [...selectedDepots] })
    setLinkSelectedSchedule({ schedule, tripIndex })
    setLinkWizardActive(true)
    setOrdersCollapsed(true)
  }
  const closeSlideUp = () => {
    setOrdersCollapsed(false)
    setLinkWizardActive(false)
    setSchedulePayload(previousPayload?.payload)
    handleDepotChange(previousPayload?.depots)
  }
  const handleDismissModal = (reload = false, newOrder, reloadDriverSchedule) => {
    setShowingOrderModal(false)

    if (newOrder?.supply_option?.delivered) {
      removeOrder(newOrder)
    }

    if (newOrder && !reload && !reloadDriverSchedule) {
      updateOrder(newOrder)
    }
    if (reloadDriverSchedule) {
      refetchDriverSchedule()
    } else if (reload) {
      refetchOrders()
      refetchDriverSchedule()
    }
    setSelectedOrder(undefined)
    setSelectedRequestOrderId(null)
    setSelectedRequestedOrder(null)
  }

  function triggerTPDModal(orderId: Gravitate.Order['id']) {
    setOrderId(orderId)
    setShowingTPDModal(true)
  }

  function handleTPDDismissModal(updatedOrder: Gravitate.Order, shouldUpdate: boolean) {
    setShowingTPDModal(false)
    setSelectedOrder(undefined)

    if (!shouldUpdate) return

    removeOrderFromCache({ queryClient, endpointKey: 'getAcceptedOrdersQuery', orderId: updatedOrder.id })
  }

  const handleOpenOptimizer = async (mode) => {
    setOptimizerMode(mode)
    setShowingOptimizerWizard(true)
  }
  const [selectedRequestedOrder, setSelectedRequestedOrder] = useState(null)
  const [selectedRequestOrderId, setSelectedRequestOrderId] = useState(null)
  const { data: requestedOrder } = useDeliveryRequestAcceptManualQuery({
    request_id: selectedRequestOrderId,
  })

  return (
    <>
      <Row>
        {userRunState?.pendingRun && (
          <OptimizedBanner setShowingOptimizerWizard={setShowingOptimizerWizard} setOptimizerMode={setOptimizerMode} />
        )}
      </Row>
      <div className='dispatch-wrapper' style={{ height: '100%' }}>
        <div className={`orders-grid-container vertical-flex  ${linkWizardActive ? 'link-wizard-active' : ''}`}>
          <OrderDrawer
            orderPreviewStoreId={orderHistoryStoreId}
            closeOrderDrawer={() => setOrderHistoryStoreId(undefined)}
            triggerModal={triggerSelection}
          />
          <OrderModal
            selectedOrder={selectedOrder}
            selectedRequestedOrder={selectedRequestedOrder}
            order={requestedOrder}
            showingModal={showingOrderModal}
            closeModal={handleDismissModal}
            updateGridOrder={updateOrder}
          />
          <ThirdPartyDispatchModal order={tpdOrder} visible={showingTPDModal} dismissModal={handleTPDDismissModal} />

          {canOptimize && (
            <OptimizerWizard
              depots={metadata?.depots || []}
              ordersFromDispatch={orders}
              visible={showingOptimizerWizard}
              scheduleData={schedules}
              setVisible={setShowingOptimizerWizard}
              schedulePayload={schedulePayload}
              mode={optimizerMode}
              setMode={setOptimizerMode}
              refetchDriverSchedule={refetchDriverSchedule}
              refetchOrders={refetchOrders}
            />
          )}
          <DSUReportDrawer
            showingDSUDrawer={showingDSUDrawer}
            setShowingDSUDrawer={setShowingDSUDrawer}
            statusUpdateRequest={statusUpdateRequest}
          />
          <UpdateHistoryDrawer selectedOrderId={auditOrderId} closeAuditDrawer={() => setAuditOrderId(undefined)} />

          {!linkWizardActive && (
            <Splitter lazy layout={isHorizontalLayout ? 'horizontal' : 'vertical'} onResize={updatePanelHeights}>
              <Splitter.Panel min={isHorizontalLayout ? '20%' : '5%'} size={`${panelHeights[0]}%`} max='95%'>
                <OrdersGrid
                  ordersGridRef={ordersGridRef}
                  setOrderHistoryStoreId={setOrderHistoryStoreId}
                  setAuditOrderId={setAuditOrderId}
                  auditOrderId={auditOrderId}
                  triggerModal={triggerSelection}
                  triggerTPDModal={triggerTPDModal}
                  requestedOrderId={selectedRequestOrderId}
                  setRequestedOrderId={setRequestedOrderId}
                  autoAcceptOrderRequest={autoAcceptOrderRequestMutation}
                  cancelOrderRequest={cancelOrderRequestMutation}
                  setSelectedRequestedOrder={setSelectedRequestedOrder}
                  config={config}
                />
              </Splitter.Panel>
              <Splitter.Panel size={`${panelHeights[1]}%`} min='5%' max='95%'>
                <Vertical>
                  <DispatchToolbar
                    openOptimizerWizard={handleOpenOptimizer}
                    setTripTimeDisplay={setTripTimeDisplay}
                    tripTimeDisplay={tripTimeDisplay}
                    config={config}
                    updatePanelHeights={updatePanelHeights}
                    panelHeights={panelHeights}
                    isHorizontalLayout={isHorizontalLayout}
                    toggleLayout={toggleLayout}
                  />

                  <TripGrid
                    ordersGridRef={ordersGridRef}
                    linkWizardActive={linkWizardActive}
                    triggerLinkWizard={triggerLinkWizard}
                    triggerModal={triggerSelection}
                    setOrderHistoryStoreId={setOrderHistoryStoreId}
                    showingDSUDrawer={showingDSUDrawer}
                    setShowingDSUDrawer={setShowingDSUDrawer}
                    loadingSchedule={loadingSchedule}
                    setStatusUpdateRequest={setStatusUpdateRequest}
                    tripTimeDisplay={tripTimeDisplay}
                    config={config}
                  />
                </Vertical>
              </Splitter.Panel>
            </Splitter>
          )}
        </div>
        {linkWizardActive && linkSelectedSchedule && (
          <OrderLinkWizard closeSlideUp={closeSlideUp} linkSelectedSchedule={linkSelectedSchedule} config={config} />
        )}
      </div>
    </>
  )
}
